/**
 * Tax arrears, on the company page.
 *
 * The card only exists when the company is on EMTA's newest debtors list. That
 * is deliberate and it is the whole design: absence here is NOT a clean bill of
 * health. `debtCents` is null both for a company that owes nothing and for every
 * company in the mirror while the arrears import has never run, and a card
 * saying "no tax debt" would be indistinguishable between the two. A missing
 * card at least says nothing at all.
 *
 * What the operator is meant to take from it: the size of the debt, whether any
 * of it is disputed (a disputed claim is not a settled fact), whether the
 * company has agreed a schedule (in which case somebody is dealing with it), and
 * how old the oldest claim is - a debt running since 2012 is a different company
 * from one that missed last month's declaration.
 */
import { AlertTriangle } from "lucide-react";
import { formatEurFull, type ArDetail } from "../../../lib/ariregister";
import { Card, fmtDate } from "./detailCard";

type T = (key: string, vars?: Record<string, string | number>) => string;

function Line({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 text-sm font-medium tabular-nums">{value}</dd>
    </div>
  );
}

export function DebtCard({ e, t }: { e: ArDetail; t: T }) {
  if (e.debtCents == null) return null;

  return (
    <Card
      title={t("ariregister.debt.title")}
      icon={AlertTriangle}
      aside={
        e.debtDataAt ? (
          <span className="text-[11px] text-muted-foreground">
            {t("ariregister.debt.asOf", { date: fmtDate(e.debtDataAt) })}
          </span>
        ) : null
      }
    >
      <div className="text-2xl font-bold tabular-nums text-destructive">{formatEurFull(e.debtCents)}</div>
      <dl className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {/* Zero is worth printing here, unlike a null elsewhere in this module:
            the source states these two explicitly for every debtor, so "0" is a
            fact rather than a gap. */}
        <Line label={t("ariregister.debt.disputed")} value={formatEurFull(e.debtDisputedCents ?? 0)} />
        <Line label={t("ariregister.debt.scheduled")} value={formatEurFull(e.debtScheduledCents ?? 0)} />
        {e.debtScheduleEndAt ? (
          <Line label={t("ariregister.debt.scheduleEnd")} value={fmtDate(e.debtScheduleEndAt)} />
        ) : null}
        {e.debtOldestDueAt ? (
          <Line label={t("ariregister.debt.oldestDue")} value={fmtDate(e.debtOldestDueAt)} />
        ) : null}
      </dl>
    </Card>
  );
}
